import CryptoJS from 'crypto-js';

/**
 * Utility to generate a stable salt from the license key.
 */
const getSalt = (licenseKey: string) => {
  return CryptoJS.SHA256(licenseKey).toString();
};

export const secureStorage = {
  /**
   * Encrypts and saves data to localStorage.
   * Formula: Ciphertext = Encrypt(LocalData, Hash(LicenseKey))
   */
  save: (licenseKey: string, storageKey: string, data: any) => {
    if (!licenseKey) throw new Error("License key required for encryption");
    
    const salt = getSalt(licenseKey);
    const jsonString = JSON.stringify(data);
    const ciphertext = CryptoJS.AES.encrypt(jsonString, salt).toString();
    
    // Store under a scoped key to avoid collision with unencrypted data
    localStorage.setItem(`secure_${storageKey}`, ciphertext);
  },

  /**
   * Loads and decrypts data from localStorage.
   */
  load: (licenseKey: string, storageKey: string) => {
    if (!licenseKey) return null;
    
    const salt = getSalt(licenseKey);
    const ciphertext = localStorage.getItem(`secure_${storageKey}`);
    
    if (!ciphertext) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, salt);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error("Decryption failed - possibly incorrect key");
      }
      
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error(`SecureStorage Error: Failed to load ${storageKey}`, error);
      return null;
    }
  },

  /**
   * Utility for device fingerprinting helper
   */
  getDeviceId: () => {
    let id = localStorage.getItem('device_fingerprint');
    if (!id) {
       // Simple version: Generate a UUID if none exists
       id = 'dev_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
       localStorage.setItem('device_fingerprint', id);
    }
    return id;
  },

  clear: (storageKey: string) => {
    localStorage.removeItem(`secure_${storageKey}`);
  }
};
